/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import api from 'api';
import { useState, useEffect } from 'react';
import {
    Outlet,
    useParams,
    useNavigate,
    useLocation,
    useResolvedPath,
    useMatch,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'redux/store';
import Modal from '@mui/material/Modal';
import { loadApp } from 'redux/application/application.middleware';
import { loadApplications } from 'redux/applications/applications-middleware';
import { setActiveApp } from 'redux/application/application.slice';
import SelectCaseDropdown from '../select-case-dropdown/SelectCaseDropdown';
import Button from '../Button';
import { useWindowSize } from 'hooks/useWindowSize';
import AgreementSignatureB2B from 'components/common/AgreementSignature/AgreementSignature';
import AssessmentAgreementSignature from 'components/common/AgreementSignature/AssessmentAgreementSignature';
import { Title, HeaderMainSection } from '../Toggle.styled';
import SelectRetainerTypeDropdown from '../select-retainer-type-dropdown/SelectRetainerTypeDropdown';
import * as S from './agreement/Agreement.styled';
import { SearchbarContainer } from '../../Dashboard.styled';
import { useTranslation } from 'react-i18next';
import SelectFormPageDropdown from '../BACKOFFICEFLOW/MYCLIENTS/components/dashboard/salesOnboarding/components/Dropdowns/SelectFormPageDropdown';
import { formCounterSlice } from 'redux/formCounter';
import { VisaPurposeServerValues } from 'data/constants/constants';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { setAlert } from 'redux/alert';
import { assessmentReportCountry } from 'utils/countries';
import { Program } from '../BACKOFFICEFLOW/MYCLIENTS/components/dashboard/salesOnboarding/components/Results/Results';
import AssessmentFlag from './components/AssessmentFlags/AssessmentFlags';
import { transformProgramsToResults } from 'utils/transformProgramsToResults';
import { useFormPath } from 'utils/useFormPath';
import { blobToDataUrl, addPageNumbersToPdf } from '../../../../utils/pdfHelpers';
import { Permission } from 'api/models/response/permissions';
import PERMISSION from '../../../../assets/permissions.json';

type NavLinkAppType = {
    id: number;
    onClick: () => void;
    name: string;
};

const CandidateDashboard = () => {
    const { id: candidateID, language_setting: language } = useSelector(
        (state: RootState) => state.user.entity
    );
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const applicationsUserId = selectedUserId || candidateID;
    const { t } = useTranslation();
    const UISAgreementTypes = [
        { name: t('eligibilityAssessment', { lng: language }) },
        { name: t('visaApplication', { lng: language }) },
    ];
    const params = useParams();
    const { id: appID } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { codes, names: enabledCountryNames } = useEnabledCountries();
    const { applications: candidateApps } = useSelector((state: RootState) => state.applications);
    const [dropdownMenuPrograms, setDropdownMenuPrograms] = useState<NavLinkAppType[]>([]);
    const [agreementTypes, setAgreementTypes] = useState([
        { name: t('eligibilityAssessment', { lng: language }) },
    ]);
    const [selectedCase, setSelectedCase] = useState(t('selectCase', { lng: language }));
    const [selectedType, setSelectedType] = useState(t('selectRetainerType', { lng: language }));
    const [isContractModalOpen, setContractModalOpen] = useState<boolean>(false);
    const [isAssessmentContractModalOpen, setAssessmentContractModalOpen] =
        useState<boolean>(false);
    const [assessmentAgreementSigned, setAssessmentAgreementSigned] = useState<boolean>(false);
    const [isFullscreenModeContractModal, setFullscreenModeContractModal] = useState<boolean>(true);
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const [customStyleForSearchbar, setCustomStyleForSearchbar] = useState(false);
    const isAgreementPage = params['*']?.includes('agreement');
    const location = useLocation();
    const isResultsPage = location.pathname.includes('/results');
    const [pdfDoc, setPdfDoc] = useState<string | null>(null);
    const [results, setResults] = useState<Program[]>([]);
    const isCandidateAssessment = params['*']?.includes('assessment');
    const { permissionsList } = useSelector((state: RootState) => state.permissions);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    useEffect(() => {
        if (width >= 1120) {
            setCustomStyleForSearchbar(false);
        }
        if (width < 1120) {
            setCustomStyleForSearchbar(true);
        }
    }, [width, height]);

    const updateApps = () => {
        if (applicationsUserId && appID) {
            dispatch(loadApplications(applicationsUserId.toString()));
            dispatch(loadApp(appID));
        }
    };

    // useEffect(() => {
    //     // set the selected case as the first available case
    //     if (candidateApps.length) {
    //         const currentPathArray = window.location.pathname.split('/');
    //         currentPathArray[currentPathArray.length - 2] = candidateApps[0].id.toString();
    //         const newPath = currentPathArray.join('/');
    //         //navigate(newPath, { replace: true });
    //     }
    // }, [candidateApps]);

    useEffect(() => {
        if (activeApp) {
            setSelectedCase(activeApp.program_name);
            setAgreementTypes(UISAgreementTypes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeApp]);

    const getTitle = () => {
        let title = '';
        if (params['*']?.includes('assessment')) {
            title = `${t('myProfile', { lng: language })}`;
        }
        if (params['*']?.includes('visa-offers')) {
            title = `${t('visaOffers', { lng: language })}`;
        }
        if (params['*']?.includes('documents')) {
            title = `${t('documents', { lng: language })}`;
        }
        if (params['*']?.includes('chats')) {
            title = `${t('chats', { lng: language })}`;
        }
        if (params['*']?.includes('agreement')) {
            if (mobile) {
                title = `${t('agreement', { lng: language })}`;
            } else {
                title = `${t('retainerAgreement', { lng: language })}`;
            }
        }
        return title;
    };

    const fetchIsAssessmentAgreementSignedData = async () => {
        try {
            const response = await api.assessmentAgreement.getAssessmentAgreementStatusForUser();
            response.data.candidate_signed_date
                ? setAssessmentAgreementSigned(true)
                : setAssessmentAgreementSigned(false);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        //! reset applications
        if (applicationsUserId) {
            dispatch(loadApplications(applicationsUserId.toString()));
        }
        if (candidateID) {
            fetchIsAssessmentAgreementSignedData();
        }
        //eslint-disable-next-line
    }, [applicationsUserId, candidateID]);

    useEffect(() => {
        if (appID) {
            dispatch(loadApp(appID));
            return;
        }
        dispatch(setActiveApp(null));
        //eslint-disable-next-line
    }, [appID]);

    useEffect(() => {
        if (candidateApps.length) {
            const convertedData = candidateApps.map<NavLinkAppType>((app) => {
                return {
                    id: app.id,
                    name: app.program_name,
                    onClick: () => {
                        const currentPathArray = window.location.pathname.split('/');
                        currentPathArray[currentPathArray.length - 2] = app.id.toString();
                        const newPath = currentPathArray.join('/');
                        navigate(newPath, { replace: true });
                    },
                };
            });

            setDropdownMenuPrograms(convertedData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateApps]);

    const openCloseContractModal = () => {
        setContractModalOpen((state) => !state);
    };

    const openCloseAssessmentContractModal = () => {
        //below line is needed to update if agreement has been signed
        fetchIsAssessmentAgreementSignedData();
        setAssessmentContractModalOpen((state) => !state);
    };

    const handleAgreementFullscreenMode = (value: boolean) => {
        setFullscreenModeContractModal(value);
    };

    const downloadAssessment = async (candId?: number) => {
        if (candId) {
            const visibleResults = transformProgramsToResults(results, enabledCountryNames);
            const country = assessmentReportCountry(
                codes,
                activeApp?.country,
                visibleResults.map((item) => item.name)
            );
            if (!country) {
                dispatch(
                    setAlert({
                        message: t('assessmentReportDownloadSubtitle', {
                            lng: language,
                            defaultValue: 'Choose the destination country and report language.',
                        }),
                        type: 'info',
                    })
                );
                return;
            }
            const resp = await api.assessment.getAssessment(candId, country, language);

            // responseType:'blob' means an error/text body also arrives as a Blob.
            // Verify the %PDF header before handing it to pdf-lib.
            const header = await resp.data.slice(0, 5).text();
            if (!header.startsWith('%PDF')) {
                const message = await resp.data.text();
                console.error('Assessment report was not a PDF:', message);
                return;
            }

            const numbered = await addPageNumbersToPdf(resp.data);
            prepareBlobFile(numbered);
        }
    };

    const prepareBlobFile = async (file: Blob) => {
        const pdfBase64 = await blobToDataUrl(new Blob([file], { type: 'application/pdf' }));
        setPdfDoc(pdfBase64);
    };

    useEffect(() => {
        if (pdfDoc) {
            const link = document.createElement('a');
            link.href = pdfDoc;
            link.download = 'assessment_report.pdf';
            link.click();
        }
        setPdfDoc(null);
    }, [pdfDoc]);
    const resolvedAssessment = useResolvedPath('assessment');

    const matchAssessment = useMatch({ path: resolvedAssessment.pathname, end: true });
    const isAssessmentPage = matchAssessment ? true : false;

    const [selectedForm, setSelectedForm] = useState('');
    const [formOptions, setFormOptions] = useState<any>([]);
    const { purpose, screen } = useSelector((state: RootState) => state.formCounter);
    const { setPurpose, set } = formCounterSlice.actions;
    const formPath = useFormPath(purpose, candidateID, language);

    const getFrenchPath = (path: string[]) => {
        const translations: { [key: string]: string } = {
            'Personal Info': 'Informations personnelles',
            'Additional Details': 'Détails supplémentaires',
            'Language Proficiency': 'Maîtrise de la langue',
            Education: 'Éducation',
            'Work Experience': "L'expérience professionnelle",
            'Job Offer': "Offre d'emploi",
            'Relatives Abroad': "Parents à l'étranger",
            'Business Ownership': "Propriété de l'entreprise",
            'Spouse/Common Law Partner': 'Époux/conjoint de fait',
            'Family Members': 'Membres de la famille',
        };

        for (let i = 0; i < path.length; i++) {
            if (path[i] in translations) {
                path[i] = translations[path[i]];
            }
        }

        return path;
    };
    useEffect(() => {
        if (language === 'FR') {
            setFormOptions(getFrenchPath(formPath));
        } else {
            setFormOptions(formPath);
        }
    }, [formPath, purpose, candidateID, language]);

    const getScreenNumber = (val: string, purpose: string) => {
        const indices = formPath?.indexOf(val);
        return indices;
    };
    const handleFormChange = (val: any) => {
        if (isResultsPage) {
            navigate(`assessment/${candidateID}`);
        }
        let screenNumber;
        setSelectedForm(val);
        if (purpose) {
            screenNumber = getScreenNumber(val, purpose) + 1;
        } else {
            screenNumber = getScreenNumber(val, '') + 1;
        }

        dispatch(set(screenNumber));
    };

    const loadData = (data: any) => {
        if (data.purpose) {
            const indexOfS = Object.values(VisaPurposeServerValues).indexOf(
                data.purpose as unknown as VisaPurposeServerValues
            );
            const key = Object.keys(VisaPurposeServerValues)[indexOfS];
            dispatch(setPurpose(key));
        }
    };

    useEffect(() => {
        if (Number(candidateID) && isAssessmentPage) {
            api.tableInput.fetchCandidateInput(loadData, 'Purpose');
        }

        if (applicationsUserId && isAgreementPage) {
            dispatch(loadApplications(applicationsUserId.toString()));
        }
        fetchBestScoreAssessmentResults(candidateID.toString(), language);
        resetScreenNumber();
        //eslint-disable-next-line
    }, [applicationsUserId, candidateID]);

    const resetScreenNumber = () => {
        if (candidateID) {
            setSelectedForm(formOptions[0]);
        } else {
            setSelectedForm('');
        }
        const screenNumber = 1;
        dispatch(set(screenNumber));
    };

    useEffect(() => {
        const currentFormName = formOptions[screen - 1];
        setSelectedForm(currentFormName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screen]);

    const fetchBestScoreAssessmentResults = async (clientId: string, lang: string) => {
        setResults([]);
        try {
            const res = await api.assessment.getTopScore(clientId, lang);
            setResults(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <>
            <HeaderMainSection>
                <Title>{getTitle()}</Title>
                {mobile &&
                    permissionsList.some(
                        (permission: Permission) => permission?.name === PERMISSION?.VIEW
                    ) && (
                        <AssessmentFlag
                            programs={transformProgramsToResults(results, enabledCountryNames)}
                        />
                    )}
                {activeApp &&
                    isAgreementPage &&
                    selectedType === t('visaApplication', { lng: language }) && (
                        <Button
                            disabled={activeApp.date_offer_sent === null}
                            background={
                                activeApp.date_offer_sent === null
                                    ? 'red'
                                    : activeApp.date_agreement_signed
                                      ? 'purple'
                                      : 'green'
                            }
                            onClick={
                                !activeApp.date_offer_sent
                                    ? () => console.log('no retainer yet')
                                    : openCloseContractModal
                            }
                        >
                            {activeApp.date_offer_sent === null
                                ? t('retainerNotReady', { lng: language })
                                : activeApp.date_agreement_signed
                                  ? t('viewAgreement', { lng: language })
                                  : t('signRetainer', { lng: language })}
                        </Button>
                    )}
                {isAgreementPage &&
                    selectedType === t('eligibilityAssessment', { lng: language }) && (
                        <Button
                            background={assessmentAgreementSigned ? 'purple' : 'green'}
                            onClick={openCloseAssessmentContractModal}
                        >
                            {assessmentAgreementSigned
                                ? t('viewAgreement', { lng: language })
                                : t('signAssessment', { lng: language })}
                        </Button>
                    )}
            </HeaderMainSection>

            <SearchbarContainer mobile={mobile}>
                {!isCandidateAssessment && (
                    <SelectCaseDropdown
                        mobile={mobile}
                        selectedCase={selectedCase}
                        handleSelectedCase={setSelectedCase}
                        agreementPage={isAgreementPage}
                        customStyleForSearchbar={customStyleForSearchbar}
                        dropdownMenuPrograms={dropdownMenuPrograms}
                    />
                )}
                {isAgreementPage && (
                    <SelectRetainerTypeDropdown
                        mobile={mobile}
                        selectedType={selectedType}
                        agreementPage={isAgreementPage}
                        handleSelectedType={setSelectedType}
                        customStyleForSearchbar={customStyleForSearchbar}
                        dropdownMenuPrograms={agreementTypes}
                    />
                )}
                {isCandidateAssessment && (
                    <SelectFormPageDropdown
                        completedForms={{}}
                        onboarding={true}
                        mobile={mobile}
                        customStyleForSearchbar={customStyleForSearchbar}
                        selectedCase={selectedForm}
                        handleSelectedCase={handleFormChange}
                        options={formOptions}
                    />
                )}
                {isResultsPage && (
                    <S.reportButton
                        onClick={() => {
                            downloadAssessment(candidateID);
                        }}
                    >
                        {t('downloadReport', { lng: language })}
                    </S.reportButton>
                )}
            </SearchbarContainer>
            {isResultsPage && <S.divider />}
            <Outlet />

            <Modal
                sx={{ zIndex: '10000' }}
                open={isContractModalOpen}
                onClose={openCloseContractModal}
            >
                <S.ModalWrapper fullscreen={isFullscreenModeContractModal}>
                    <S.Modal1 fullscreen={isFullscreenModeContractModal}>
                        <S.BtnWrapper>
                            <S.IconBtn1 onClick={openCloseContractModal}>
                                <S.CrossSimpleIcon1 />
                            </S.IconBtn1>
                        </S.BtnWrapper>
                        <AgreementSignatureB2B
                            appID={activeApp ? activeApp.id.toString() : null}
                            onFullscreenMode={handleAgreementFullscreenMode}
                            onUserSignedAgreement={() => {
                                updateApps();
                                openCloseContractModal();
                            }}
                        />
                    </S.Modal1>
                </S.ModalWrapper>
            </Modal>

            <Modal
                sx={{ zIndex: '10000' }}
                open={isAssessmentContractModalOpen}
                onClose={openCloseAssessmentContractModal}
            >
                <S.ModalWrapper fullscreen={isFullscreenModeContractModal}>
                    <S.Modal1 fullscreen={isFullscreenModeContractModal}>
                        <S.BtnWrapper>
                            <S.IconBtn1 onClick={openCloseAssessmentContractModal}>
                                <S.CrossSimpleIcon1 />
                            </S.IconBtn1>
                        </S.BtnWrapper>
                        <AssessmentAgreementSignature
                            isClientFlow={true}
                            userID={candidateID.toString()}
                            onFullscreenMode={handleAgreementFullscreenMode}
                            onUserSignedAgreement={openCloseAssessmentContractModal}
                        />
                    </S.Modal1>
                </S.ModalWrapper>
            </Modal>
        </>
    );
};

export default CandidateDashboard;
